import { SectionCard } from "@/components/common/SectionCard";
import type { ServiceCalendarListScope } from "@/lib/service/calendarLocalWeek";
import type { ServiceEvent, ServiceRig, ServiceTech, ServiceWorkQueue } from "@/lib/service/types";
import {
  EVENT_STATUS_CHIP,
  EVENT_STATUS_LABELS,
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
  queueLabel,
  rigLabel,
  techLabel,
} from "./serviceShared";

type Props = {
  scope: ServiceCalendarListScope;
  title: string;
  events: ServiceEvent[];
  selectedDay: string | null;
  queues: ServiceWorkQueue[];
  rigs: ServiceRig[];
  techs: ServiceTech[];
  schedulableEvents: ServiceEvent[];
  selectedEventId: string | null;
  onSelectEvent: (eventId: string) => void;
  scheduleDate: string;
  scheduleWindow: string;
  scheduleRigId: string;
  scheduleTechId: string;
  onScheduleDateChange: (v: string) => void;
  onScheduleWindowChange: (v: string) => void;
  onScheduleRigChange: (v: string) => void;
  onScheduleTechChange: (v: string) => void;
  onSchedule: () => void;
  actionBusy: boolean;
};

function formatEventDate(date: string): string {
  return new Date(date + "T12:00:00").toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function emptyMessage(scope: ServiceCalendarListScope): string {
  if (scope === "all_unscheduled") return "No unscheduled events in this queue filter.";
  if (scope === "all_scheduled") return "No scheduled events in this queue filter.";
  return "No scheduled events this day.";
}

export function ServiceCalendarDayEvents({
  scope,
  title,
  events,
  selectedDay,
  queues,
  rigs,
  techs,
  schedulableEvents,
  selectedEventId,
  onSelectEvent,
  scheduleDate,
  scheduleWindow,
  scheduleRigId,
  scheduleTechId,
  onScheduleDateChange,
  onScheduleWindowChange,
  onScheduleRigChange,
  onScheduleTechChange,
  onSchedule,
  actionBusy,
}: Props) {
  const showScheduleForm = scope === "day" || scope === "all_unscheduled";
  const dayScopeNeedsSelection = scope === "day" && !selectedDay;

  if (dayScopeNeedsSelection) {
    return (
      <SectionCard title={title}>
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          Select a day on the week grid to see and schedule events.
        </p>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={title}>
      {events.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">{emptyMessage(scope)}</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-zinc-700 mb-4">
          {events.map((ev) => {
            const selected = ev.id === selectedEventId;
            const chipClass = EVENT_STATUS_CHIP[ev.status] ?? EVENT_STATUS_CHIP.unscheduled;
            const showDate = scope === "all_scheduled" && ev.scheduled_date;
            return (
              <li
                key={ev.id}
                className={`flex flex-wrap items-start gap-2 py-3 ${
                  selected ? "bg-indigo-50 dark:bg-indigo-950/30 -mx-2 px-2 rounded" : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
                    {ev.title || ev.location_label}
                    <span
                      className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${chipClass}`}
                    >
                      {EVENT_STATUS_LABELS[ev.status] ?? ev.status}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-zinc-400">
                    {showDate ? `${formatEventDate(ev.scheduled_date!)} · ` : ""}
                    {queueLabel(queues, ev.queue_id)} · {ev.scheduled_window_label ?? "—"}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-zinc-400">
                    Tech: {techLabel(techs, ev.tech_id)} · Rig: {rigLabel(rigs, ev.assigned_rig_id)}
                  </div>
                </div>
                {showScheduleForm ? (
                  <button
                    type="button"
                    className={ghostBtnClass}
                    onClick={() => onSelectEvent(ev.id)}
                    aria-pressed={selected}
                  >
                    {selected ? "Close" : "Schedule"}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {showScheduleForm ? (
        <div className="border-t border-gray-100 dark:border-zinc-700 pt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500 mb-2">
            Schedule an event
          </div>
          {schedulableEvents.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">No unscheduled events in this queue filter.</p>
          ) : (
            <>
              <select
                className={`${inputClass} mb-2`}
                value={selectedEventId ?? ""}
                onChange={(e) => onSelectEvent(e.target.value)}
              >
                <option value="">Pick event…</option>
                {schedulableEvents.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title || ev.location_label || ev.id.slice(0, 8)}
                  </option>
                ))}
              </select>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                <input
                  type="date"
                  className={inputClass}
                  value={scheduleDate}
                  onChange={(e) => onScheduleDateChange(e.target.value)}
                />
                <input
                  className={inputClass}
                  value={scheduleWindow}
                  onChange={(e) => onScheduleWindowChange(e.target.value)}
                  placeholder="Window label"
                />
                <select
                  className={inputClass}
                  value={scheduleTechId}
                  onChange={(e) => onScheduleTechChange(e.target.value)}
                >
                  <option value="">Tech</option>
                  {techs.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.display_name}
                    </option>
                  ))}
                </select>
                <select
                  className={inputClass}
                  value={scheduleRigId}
                  onChange={(e) => onScheduleRigChange(e.target.value)}
                >
                  <option value="">Rig</option>
                  {rigs.map((rig) => (
                    <option key={rig.id} value={rig.id}>
                      {rig.designation}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={primaryBtnClass}
                  disabled={actionBusy || !selectedEventId || !scheduleDate}
                  onClick={onSchedule}
                >
                  {actionBusy ? "Saving…" : "Save schedule"}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </SectionCard>
  );
}
